import { ThemeOptions } from "@mui/material";
import type { } from '@mui/x-date-pickers/themeAugmentation';
import { AppImages } from "app/appImages";
import { IThemeDefinition } from "themes";

export const buildTheme = (options: IThemeDefinition): ThemeOptions => ({
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 1088,
            lg: 1280,
            xl: 1536,
        },
    },
    zIndex: {
        // Drawer sempre na "frente" de um diálogo
        drawer: 1300
    },
    typography: {
        h1: {
            fontWeight: options.font.bold,
            fontSize: options.font.sizeXl,
            textTransform: "uppercase",
            lineHeight: "normal"
        },
        h2: {
            fontWeight: options.font.bold,
            fontSize: options.font.sizeLg,
            textTransform: "uppercase",
            lineHeight: "normal"
        },
        h3: {
            fontWeight: options.font.bold,
            fontSize: options.font.sizeMd,
            textTransform: "uppercase",
            lineHeight: "normal"
        },
        h4: {
            fontWeight: options.font.medium,
            fontSize: options.font.sizeMd,
            textTransform: "uppercase",
            lineHeight: "normal"
        },
        h5: {
            fontWeight: options.font.medium,
            fontSize: options.font.sizeMd,
            color: options.color.mutedDark,
            lineHeight: "20px"
        },
        h6: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeSm,
            color: options.color.mutedDark,
            lineHeight: "normal"
        },
        overline: {
            fontWeight: options.font.bold,
            fontSize: options.font.sizeSm,
            lineHeight: "20px",
            letterSpacing: "inherit",
            textTransform: "uppercase"
        },
        body1: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeMd,
            color: options.color.black,
            lineHeight: "24px"
        },
        body2: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeMd,
            color: options.color.mutedMain,
            lineHeight: "20px"
        },
        bodyNotice: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeXs,
            lineHeight: "normal",
            color: options.color.warningMain
        },
        subtitle1: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeSm,
            color: options.color.mutedMain,
            lineHeight: "20px"
        },
        subtitle2: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeXxs,
            lineHeight: "20px"
        },
        caption: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeXs,
            color: options.color.mutedMain,
            lineHeight: "normal"
        },
        captionHeader: {
            fontWeight: options.font.regular,
            fontSize: options.font.sizeXs,
            lineHeight: "normal",
            textTransform: "uppercase"
        },
        iconText: {
            display: "flex",
            justifyContent: "center",
            color: options.color.mutedMain,
            "& span": {
                fontSize: "14px",
                alignSelf: "center",
                paddingLeft: .5
            },
            "& svg": {
                fontSize: "20px",
            }
        },
        iconTextHighlight: {
            display: "flex",
            justifyContent: "center",
            color: options.color.highlightMain,
            "& span": {
                fontSize: "14px",
                alignSelf: "center",
                paddingLeft: .5
            },
            "& svg": {
                fontSize: "20px",
            }
        },
        linkAction: {
            color: options.color.primaryMain,
            fontSize: options.font.sizeSm,
            fontWeight: options.font.regular,
            "&:visited": {
                color: options.color.primaryMain,
            },
            "&:hover": {
                color: options.color.highlightMain,
            },
            "&:focus": {
                color: options.color.highlightMain,
            }
        },
        linkActionHeader: {
            textTransform: "uppercase",
            color: options.color.primaryMain,
            fontSize: options.font.sizeSm,
            fontWeight: options.font.regular,
            "&:visited": {
                color: options.color.primaryMain,
            },
            "&:hover": {
                color: options.color.highlightMain,
            },
            "&:focus": {
                color: options.color.highlightMain,
            }
        },
        linkMenu: {
            color: options.color.highlightMain,
            fontSize: options.font.sizeSm,
            fontWeight: options.font.regular,
            "&:visited": {
                color: options.color.highlightMain,
            },
            "&:hover": {
                color: options.color.infoMain,
            },
            "&:focus": {
                color: options.color.highlightMain,
            }
        },
        indicator: {
            fontSize: options.font.sizeXxl,
            textTransform: "uppercase"
        },
    },

    palette: {
        background: {
            default: options.color.white,
            paper: options.color.white
        },
        primary: {
            main: options.color.primaryMain,
            light: options.color.highlightMain,
            dark: options.color.primaryDark
        },
        secondary: {
            main: options.color.secondaryMain,
            light: options.color.secondaryLight,
            dark: options.color.secondaryDark
        },
        error: {
            main: options.color.errorMain,
            light: options.color.errorLight,
            dark: options.color.errorDark
        },
        warning: {
            main: options.color.warningMain,
            light: options.color.warningLight,
            dark: options.color.warningDark,
            contrastText: options.color.white
        },
        info: {
            main: options.color.infoMain,
            light: options.color.infoLight,
            dark: options.color.infoDark
        },
        success: {
            main: options.color.successMain,
            light: options.color.successLight,
            dark: options.color.successDark,
            contrastText: options.color.white
        },
        notice: {
            main: options.color.noticeMain,
            light: options.color.noticeLight,
            dark: options.color.noticeDark
        },
        paper: {
            main: options.color.paperMain,
            light: options.color.white,
            dark: options.color.paperDark
        },
        muted: {
            main: options.color.mutedMain,
            light: options.color.mutedLight,
            dark: options.color.mutedDark,
            contrastText: options.color.white
        },
        highlight: {
            main: options.color.highlightMain,
            light: options.color.highlightLight,
            dark: options.color.highlightDark
        },
        cancel: {
            main: options.color.cancelMain,
            light: options.color.cancelLight,
            dark: options.color.cancelDark,
            contrastText: options.color.white
        },
        chip: {
            main: options.color.chipMain,
            light: options.color.chipLight,
            dark: options.color.chipDark,
        },
        ai: {
            main: options.color.ai,
            light: options.color.ai,
            dark: options.color.ai
        }
    },

    extended: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: options.font.regular,
            fontSize: options.font.sizeSm,
            lineHeight: "1.5em",
            color: options.color.mutedMain
        },

        feed: {
            classification: {
                fontWeight: options.font.bold,
                fontSize: "11px",
                color: options.color.mutedLight,
                textTransform: "uppercase",
            },
            largeTitle: {
                fontWeight: options.font.bold,
                fontSize: "24px",
                color: options.color.black,
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            title: {
                fontWeight: options.font.bold,
                fontSize: "18px",
                color: options.color.black,
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            authorPrimary: {
                fontWeight: options.font.bold,
                fontSize: "14px",
                color: options.color.mutedMain
            },
            authorSecondary: {
                fontSize: "12px",
                color: options.color.mutedMain
            },
            authorCaption: {
                fontWeight: options.font.bold,
                fontSize: "14px",
                color: options.color.mutedMain,
                textTransform: "uppercase"
            },
            content: {
                fontSize: "14px",
                color: options.color.black,
                lineHeight: "20px"
            },
            taskHeader: {
                fontSize: "12px",
                color: options.color.mutedMain,
            },
            taskContent: {
                fontSize: "14px",
                color: options.color.black,
            },
            action: {
                fontSize: "14px",
                color: options.color.mutedMain,
            },
            moreContent: {
                fontSize: "12px",
                color: options.color.mutedMain,
                textTransform: "uppercase",
                fontWeight: "bold"
            },
            body1: {
                color: options.color.mutedMain,
                fontSize: "14px",
            },
            body2: {
                color: options.color.mutedMain,
                fontSize: "12px",
            },
            body3: {
                fontSize: "12px",
                color: options.color.black,
                textTransform: "uppercase",
                fontWeight: "bold"
            },
            sticker: {
                fontSize: "11px",
                textTransform: "uppercase",
            },
            stickerLowercase: {
                fontSize: "11px",
            }
        },

        lmsCard: {
            caption: {
                fontWeight: options.font.regular,
                fontSize: options.font.sizeXxs,
                color: options.color.mutedDark,
                textTransform: "uppercase",
                lineHeight: 1.6
            },
            title: {
                fontWeight: options.font.bold,
                fontSize: options.font.sizeMd,
                color: options.color.black,
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: "unset"
            },
            titleAlt: {
                fontWeight: options.font.bold,
                fontSize: options.font.sizeMd,
                color: options.color.black,
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: "unset"
            },
            name: {
                fontWeight: options.font.regular,
                fontSize: options.font.sizeSm,
                color: options.color.black,
                lineHeight: 1.6,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            iconText: {
                fontWeight: options.font.regular,
                fontSize: options.font.sizeSm,
                color: options.color.mutedMain,
                lineHeight: 1.6,
            },
            link: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                fontWeight: options.font.bold,
                fontSize: options.font.sizeXs,
                color: options.color.highlightMain,
                textTransform: "uppercase"
            },
            linkAlt: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                fontWeight: options.font.bold,
                fontSize: options.font.sizeMd,
                color: options.color.highlightMain,
                textTransform: "uppercase"
            },
            line: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                fontSize: options.font.sizeXs,
                lineHeight: "14px",
                color: options.color.mutedLight,
            },
            lineWrap: {
                overflow: 'unset',
                textOverflow: 'unset',
                whiteSpace: "unset",
                fontSize: options.font.sizeXs,
                lineHeight: 1.6,
                color: options.color.mutedMain
            },
            sticker: {
                textAlign: "center",
                fontWeight: options.font.regular,
                fontSize: options.font.sizeXs,
                textTransform: "uppercase",
                width: "100%",
                padding: 0.1,
            }
        },

        tab: {
            tab: {
                color: options.color.mutedDark,
                fontWeight: options.font.medium,
                fontSize: options.font.sizeMd,
            },
            selected: {
                fontWeight: options.font.bold,
                color: options.color.highlightMain,
            },
            indicator: {
                height: "4px",
                backgroundColor: options.color.highlightMain,
            },
            container: {
                color: options.color.mutedDark,
                borderBottom: "solid 1px",
            },
            large: { fontSize: "18px" },
            medium: { fontSize: "16px", fontWeight: "bold" },
            small: { fontSize: "18px" }
        },

        chipTab: {
            tabs: {
                minHeight: "unset",
            },
            tab: {
                color: options.color.black,
                opacity: 1,
                fontSize: options.font.sizeSm,
                textTransform: "uppercase",
                borderRadius: "16px",
                backgroundColor: options.color.chipMain,
                padding: "4px 24px",
                minHeight: "unset"
            },
            selected: {
                color: options.color.white,
                fontWeight: "bold",
                backgroundColor: options.color.highlightMain,
            },
            indicator: {
                height: 0
            },
            container: {
                gap: "16px",
            },
            large: "18px",
            medium: "16px",
            small: "13px"
        },

        ribbonTab: {
            root: {
                color: options.color.mutedMain,
                fontWeight: 700,
                fontSize: "16px",
                textTransform: "unset"
            },
            selected: {
                fontWeight: "bold"
            },
            indicator: {
                height: "2px",
                backgroundColor: options.color.mutedMain,
            },
            container: {
                color: options.color.mutedMain,
            },
        },

        link: {
            default: {
                textDecoration: "none",
                cursor: "pointer",
                color: "inherit"
            },
            active: {
                color: "inherit"
            },
            hover: {
                textDecoration: "none",
                color: options.color.infoMain
            },
            visited: {
                color: "inherit"
            },
            focus: {
            }
        },

        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold",
                    textDecoration: "none"
                }
            },
            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic",
                    textDecoration: "none"
                }
            }
        },

        separatorBarText: {
            root: {
                textTransform: "uppercase",
                borderRadius: "8px",
                backgroundColor: options.color.paperDark,
                color: options.color.mutedMain,
                padding: "8px",
                fontWeight: "bold"
            }
        },

        workArea: {
            background: options.color.workAreaBackground,
            borderColor: options.color.workAreaBorder,
            body: {
                maxWidth: "1920px",
                margin: "0 auto",
                padding: 0,
                height: "100vh",
                backgroundColor: options.color.workAreaBodyBackgound,
                overflowX: "hidden"
            },
            header: {
                container: {
                    display: "flex",
                    backgroundImage: `linear-gradient(90deg, ${options.color.white} 5%, ${options.color.primaryDark} 100%)`,
                    paddingLeft: "16px",
                    paddingRight: "16px",

                },
                iconButton: {
                    color: options.color.white
                },
            },
            sideBar: {
                container: {
                    color: options.color.mutedMain,
                    backgroundColor: options.color.white,
                    fontSize: "24px",
                    paddingTop: "16px",
                    width: "44px"
                },
                selectedColor: options.color.highlightMain,
                separatorColor: options.color.workAreaBorder,
            },
            navigationBar: {
                separatorColor: options.color.separator
            },
            content: {
            },
            footer: {
                backgroundColor: options.color.paperDark,
                marginTop: "1px",
                paddingTop: "2px",
                paddingLeft: "8px",
                paddingRight: "8px",
            },
            lateralMenu: {
                minHeight: "100px",
                padding: "16px",
                marginLeft: "16px",
                borderRadius: "16px",
                backgroundColor: options.color.white,
                boxShadow: "2px 2px 20px rgba(0,0,0,0.2)"
            },
            rightDrawer: {
                width: "350px",
                backgroundColor: options.color.white
            },
            wrapperMaxWidth: "1024px"
        }
    },

    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: "4px",
                    fontSize: options.font.sizeSm,
                    fontWeight: "bold",
                    lineHeight: "unset"
                },
                sizeLarge: {
                    fontSize: "16px"
                },
                sizeMedium: {
                    fontsize: "14px"
                },
                sizeSmall: {
                    fontSize: "12px"
                }
            },
            defaultProps: {
            }
        },
        MuiAutocomplete: {
            defaultProps: {
                autoCorrect: "off",
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: "standard",
                autoComplete: "off",
                autoCorrect: "off",
            }
        },
        MuiInput: {
            defaultProps: {
                autoComplete: "off",
                autoCorrect: "off",
            },
            styleOverrides: {
                input: {
                    color: options.color.black
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                filled: {
                    color: options.color.black
                }
            }
        },
        MuiInputLabel: {
            defaultProps: {
                variant: "standard",
            },
            styleOverrides: {
                standard: {
                    color: options.color.mutedMain,
                    fontWeight: options.font.regular,
                    fontSize: options.font.sizeMd,
                },
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: options.color.mutedMain,
                    fontWeight: options.font.regular,
                    fontSize: options.font.sizeMd,
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    color: options.color.mutedMain,
                    fontWeight: options.font.regular,
                    fontSize: options.font.sizeMd,
                }
            }
        },
        MuiFormHelperText: {
            defaultProps: {
                component: "div"
            },
            styleOverrides: {
                root: {
                    color: options.color.mutedMain,
                    fontWeight: options.font.regular,
                    fontSize: options.font.sizeXs,
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontSize: '0.875rem',
                    lineHeight: 1.43,
                    letterSpacing: '0.01071em',
                },
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: options.color.secondaryMain,
                    height: "4px"
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                "root": {
                    "&.Mui-disabled": {
                        color: options.color.mutedLight
                    },
                    color: options.color.mutedMain
                }
            }
        },

        MuiLink: {
            defaultProps: {
                underline: 'none',
                color: options.color.highlightMain,
                fontSize: options.font.sizeSm
            },
            styleOverrides: {
                root: {
                    ":hover": {
                        color: options.color.primaryMain,
                    },
                    ":visited": {
                        color: options.color.primaryMain,
                    }
                },
                underlineNone: {
                    textDecoration: "none",
                },
                underlineHover: {
                    textDecoration: "none"
                }
            }
        },
        MuiSelect: {
            defaultProps: {
                variant: "standard"
            }
        },
        MuiChip: {
            styleOverrides: {
                colorPrimary: {
                    color: options.color.black,
                    backgroundColor: options.color.chipMain,
                },
                deleteIconColorPrimary: {
                    color: options.color.chipDark,
                },
                colorSecondary: {
                    color: options.color.white,
                    backgroundColor: options.color.mutedMain,
                },
                sizeMedium: {
                    fontSize: options.font.sizeMd,
                    fontWeight: "normal",
                },
                sizeSmall: {
                    fontSize: "13px",
                    fontWeight: "normal",
                }
            },
            defaultProps: {
                color: "primary",
            },
            variants: [
                {
                    props: { size: 'large' },
                    style: {
                        fontSize: "18px",
                        fontWeight: "normal",
                    },
                },
            ],
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: options.color.mutedMain,
                    fontSize: options.font.sizeSm,
                    fontWeight: options.font.regular,
                    lineHeight: options.font.sizeSm
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    color: options.color.mutedMain
                },
            }
        },
        MuiDialogTitle: {
            defaultProps: {
                variant: "h6"
            }
        },

        MuiAccordion: {
            styleOverrides: {
                root: {
                    color: "unset"
                },
            }
        },
        MuiCard: {
            defaultProps: {
                elevation: 4,
            },
            styleOverrides: {
                root: {
                    color: options.color.mutedMain,
                    borderRadius: "8px",
                    marginTop: "4px",
                }
            }
        },
        MuiCardHeader: {
            defaultProps: {
                titleTypographyProps: {
                    fontWeight: options.font.bold,
                    fontSize: options.font.sizeLg,
                    color: options.color.mutedMain,
                    textTransform: "uppercase"
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    paddingLeft: "16px",
                    paddingRight: "16px"
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    zIndex: 1301
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: 8
                }
            }
        },
        MuiAvatar: {
            defaultProps: {
                src: AppImages.EmptyImageThumbnail
            },
            styleOverrides: {
                rounded: {
                    borderRadius: 8
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: options.font.sizeSm
                }
            }
        },
        MuiPickersTextField: {
            defaultProps: {
                variant: "standard",
                InputLabelProps: {
                    sx: {
                        maxWidth: "calc(100% - 16px)",
                        "&.Mui-focused": { maxWidth: "100%" },
                    }
                },
            }
        }
    },
});
